import type { NativeJourney } from "../../shared/native-bridge";

/**
 * What the lock screen and Dynamic Island would show, drawn in the browser.
 *
 * Rendered from the **same `NativeJourney` the bridge sends**, not from a second reading of the
 * plan — so the preview cannot drift from the thing it is previewing. If the words are wrong here
 * they are wrong on the phone.
 *
 * It is a drawing, not a simulation. iOS renders the real one, counts the real countdown down
 * itself, and decides on its own when to grey it out. This exists so the layout can be argued
 * about without a rebuild and a reinstall for every change.
 */
export function ActivityPreview({ journey, nowMs }: { journey: NativeJourney; nowMs: number }) {
	const remainingMs = journey.countdownToMs == null ? null : journey.countdownToMs - nowMs;
	const value = journey.value ?? countdownText(remainingMs);
	const tone = journey.tone ? ` is-${journey.tone}` : "";

	return (
		<div className="lapreview">
			<div className="lapreview__phone">
				<span className="lapreview__caption">Lock screen</span>
				<div className="lapreview__lock">
					<div className="lapreview__stack">
						<span className="lapreview__lead">{journey.lead}</span>
						<div className="lapreview__row">
							<span className={`lapreview__count${tone}`}>{value}</span>
							{journey.note ? <span className={`lapreview__note${tone}`}>{journey.note}</span> : null}
						</div>
						{journey.detail ? <span className="lapreview__detail">{journey.detail}</span> : null}
					</div>
				</div>
			</div>

			<div className="lapreview__phone">
				<span className="lapreview__caption">Dynamic Island — compact</span>
				<div className="lapreview__island lapreview__island--compact">
					{/* The phone draws an SF Symbol here. A browser cannot, so name the leg instead: what
					    this has to prove is that the right one was chosen, not what it looks like. */}
					<span className={`lapreview__glyph${tone}`}>{journey.glyph || "\u2014"}</span>
					<span className={`lapreview__countSmall${tone}`}>{value}</span>
				</div>
			</div>

			<div className="lapreview__phone">
				<span className="lapreview__caption">Dynamic Island — expanded</span>
				<div className="lapreview__island lapreview__island--expanded">
					{/* Lead then number then detail: reading it as lead, detail, number was the complaint. */}
					<div className="lapreview__row">
						<span className="lapreview__lead">{journey.leadShort}</span>
						<span className={`lapreview__count${tone}`}>{value}</span>
					</div>
					<div className="lapreview__row lapreview__row--under">
						{journey.detail ? <span className="lapreview__detail">{journey.detail}</span> : null}
						{journey.note ? <span className={`lapreview__note${tone}`}>{journey.note}</span> : null}
					</div>
				</div>
			</div>

			<p className="field__hint">
				{journey.phase === "planning"
					? "Phase is “planning”, so no activity would be running at all — this is what it would say if one were."
					: `Phase “${journey.phase}”. An activity would be running.`}
			</p>
		</div>
	);
}

/**
 * How long is left, read the way the hero card reads it.
 *
 * Whole minutes for as long as minutes are the useful unit, and seconds only once they are: at two
 * minutes out "1:45" is worth having and "2 min" is not. Below that the phone hands the number to
 * the system, which ticks it on a locked screen; above it the minutes are only as fresh as the last
 * update, and the card greys out rather than pretending otherwise.
 */
export function countdownText(remainingMs: number | null): string {
	if (remainingMs == null) {
		return "\u2014";
	}
	// Gone, so how long gone — the same negative minutes the card shows. "now" was the same word for
	// a train pulling in and one eight minutes down the track.
	if (remainingMs <= 0) {
		return `${Math.round(remainingMs / 60_000)} min`;
	}
	const minutes = Math.round(remainingMs / 60_000);
	return minutes >= 60 ? `${Math.floor(minutes / 60)}h ${minutes % 60}m` : `${minutes} min`;
}
